/**
 * Public prestataire-facing contract signing page — Iter 73.
 *
 * URL: /permanent-sign/:token
 * No auth — the token IS the auth. Mirrors ContractSign.jsx (salariés)
 * so the CEO gets an identical UX for both populations.
 *
 * UX:
 *  1. Hero + summary card (identity, region, period)
 *  2. PDF preview embedded in an iframe
 *  3. Signature canvas (mouse + touch, retina-crisp, clear button)
 *  4. "Lu et approuvé - Bon pour accord" checkbox
 *  5. Signer button (disabled until both signature + checkbox present)
 *  6. Success screen with SHA-256 fingerprint
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    Briefcase,
    CalendarClock,
    CheckCircle2,
    Download,
    Eraser,
    FileText,
    Loader2,
    Lock,
    MapPin,
    ShieldCheck,
    Sparkles,
    UserRound,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/public/permanent-contracts`;


/* ─────────────────────────── Signature canvas (mirrored from ContractSign) */

function SignaturePad({ onChange, disabled }) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPtRef = useRef(null);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#1B1B1B";
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
    }, []);

    const eventToPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        if (e.touches && e.touches[0]) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        if (disabled) return;
        e.preventDefault();
        drawingRef.current = true;
        lastPtRef.current = eventToPoint(e);
    };
    const move = (e) => {
        if (!drawingRef.current || disabled) return;
        e.preventDefault();
        const p = eventToPoint(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastPtRef.current = p;
        if (empty) setEmpty(false);
    };
    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        lastPtRef.current = null;
        const dataUrl = canvasRef.current.toDataURL("image/png");
        onChange(dataUrl, false);
    };
    const clear = () => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
        setEmpty(true);
        onChange(null, true);
    };

    return (
        <div className="rounded-3xl bg-white border-2 border-slate-200 p-1.5">
            <canvas
                ref={canvasRef}
                data-testid="signature-canvas"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                className={`w-full h-48 rounded-2xl bg-white cursor-crosshair touch-none ${disabled ? "opacity-50" : ""}`}
                style={{ display: "block" }}
            />
            <div className="flex items-center justify-between px-3 py-1.5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {empty ? "Signez ici avec votre doigt ou votre souris" : "Signature capturée ✓"}
                </div>
                <button
                    type="button"
                    onClick={clear}
                    disabled={disabled || empty}
                    data-testid="signature-clear"
                    className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-red-600 transition disabled:opacity-40"
                >
                    <Eraser className="h-3 w-3" /> Effacer
                </button>
            </div>
        </div>
    );
}


/* ─────────────────────────── Page */

const REGION_LABEL = {
    idf: "Île-de-France",
    nouvelle_aquitaine: "Nouvelle-Aquitaine",
    gironde: "Nouvelle-Aquitaine",
    occitanie: "Occitanie",
    paca: "Provence-Alpes-Côte d'Azur",
    grand_est: "Grand Est",
};


export default function PermanentContractSign() {
    const { token } = useParams();
    const [state, setState] = useState({ status: "loading", data: null, error: null });
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [approved, setApproved] = useState(false);
    const [signing, setSigning] = useState(false);
    const [signError, setSignError] = useState(null);
    const [justSigned, setJustSigned] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API}/${token}`);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setState({ status: "error", data: null, error: body.detail || "Lien invalide" });
                return;
            }
            const data = await res.json();
            setState({ status: "ready", data, error: null });
        } catch {
            setState({ status: "error", data: null, error: "Connexion impossible" });
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const sign = async () => {
        if (!signatureDataUrl || !approved || signing) return;
        setSigning(true);
        setSignError(null);
        try {
            const res = await fetch(`${API}/${token}/sign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    signature_data_url: signatureDataUrl,
                    approval_confirmed: approved,
                }),
            });
            const j = await res.json();
            if (!res.ok) {
                setSignError(j.detail || "Erreur lors de la signature");
                return;
            }
            setJustSigned({ signed_at: j.signed_at, sha256: j.sha256 });
            await load();
        } catch (e) {
            setSignError(String(e));
        } finally {
            setSigning(false);
        }
    };

    if (state.status === "loading") {
        return (
            <div className="min-h-screen bg-shariti-cream flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }
    if (state.status === "error") {
        return (
            <div className="min-h-screen bg-shariti-cream flex flex-col items-center justify-center px-6" data-testid="permanent-contract-load-error">
                <AlertCircle className="h-10 w-10 text-shariti-red mb-3" />
                <div className="text-lg font-black text-shariti-black">{state.error}</div>
                <p className="text-sm font-medium text-slate-500 mt-1 text-center max-w-md">
                    Contactez votre référent Shariti pour obtenir un nouveau lien.
                </p>
            </div>
        );
    }

    const c = state.data;
    const isSigned = c.is_signed || justSigned;
    const fullname = `${c.first_name} ${c.last_name}`.trim();

    return (
        <div className="min-h-screen bg-shariti-cream pb-40" data-testid="permanent-contract-page">
            <header className="bg-shariti-black text-white">
                <div className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange/20 text-shariti-orange px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-4">
                        <FileText className="h-3 w-3" /> Contrat de prestation Shariti
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black tracking-tight">
                        {isSigned ? (
                            <>Contrat <span className="text-shariti-orange">signé</span> ✓</>
                        ) : (
                            <>Prêt à signer,<br />{c.first_name}<span className="text-shariti-orange">.</span></>
                        )}
                    </h1>
                    <p className="mt-3 text-sm md:text-base text-white/80 font-medium max-w-2xl">
                        {isSigned
                            ? "Merci — votre contrat est archivé et une copie PDF vous a été envoyée par email."
                            : "Relisez soigneusement votre contrat de prestation ci-dessous. Une fois signé, une copie PDF vous sera envoyée automatiquement à votre email."}
                    </p>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 md:px-8 mt-6 space-y-4">
                <section className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5 md:p-7">
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-3 flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3" /> Récapitulatif
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryTile Icon={UserRound} label="Prestataire" value={`${c.civilite} ${fullname}`} />
                        <SummaryTile Icon={Briefcase} label="Statut" value="Autoentrepreneur" />
                        <SummaryTile Icon={MapPin} label="Région" value={REGION_LABEL[c.primary_region] || c.primary_region} />
                        <SummaryTile Icon={CalendarClock} label="Période" value={`${fr(c.start_date)} → ${fr(c.end_date)}`} />
                    </div>
                    <div className="mt-3 text-[11px] font-semibold text-slate-500">
                        Rémunération selon la grille bulletins nets (35 / 50 / 65 / 80 €) définie à l&apos;article 4.
                    </div>
                </section>

                <section className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="px-5 md:px-7 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-black flex items-center gap-1.5">
                            <FileText className="h-3 w-3 text-shariti-orange" />
                            {isSigned ? "Contrat signé — Aperçu" : "Contrat à signer — Aperçu"}
                        </div>
                        <a
                            href={`${API}/${token}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="permanent-contract-download"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 h-8 px-3 text-[11px] font-bold"
                        >
                            <Download className="h-3 w-3" /> Télécharger PDF
                        </a>
                    </div>
                    <iframe
                        title="Contrat prestation PDF"
                        src={`${API}/${token}/pdf`}
                        data-testid="permanent-contract-pdf-iframe"
                        className="w-full h-[520px] md:h-[680px] bg-slate-100"
                    />
                </section>

                {!isSigned ? (
                    <section className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5 md:p-7">
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-3 flex items-center gap-1.5">
                            <ShieldCheck className="h-3 w-3" /> Votre signature
                        </div>
                        <p className="text-[13px] font-medium text-slate-600 mb-3">
                            Dessinez votre signature dans le cadre ci-dessous. Elle sera intégrée dans le contrat final,
                            reportée en paraphe au bas de chaque page, et scellée cryptographiquement (SHA-256).
                        </p>
                        <SignaturePad onChange={(url, empty) => setSignatureDataUrl(empty ? null : url)} />
                        <label className="mt-5 flex items-start gap-2.5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={approved}
                                onChange={(e) => setApproved(e.target.checked)}
                                data-testid="approval-checkbox"
                                className="mt-1 h-5 w-5 accent-shariti-orange shrink-0"
                            />
                            <span className="text-[13px] font-semibold text-shariti-black">
                                J&apos;ai lu l&apos;intégralité du contrat et j&apos;accepte ses termes.
                                <span className="italic font-normal text-slate-500 ml-1">
                                    (Équivalent numérique de la mention « lu et approuvé - Bon pour accord »)
                                </span>
                            </span>
                        </label>
                        {signError && (
                            <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-3 text-sm font-bold text-red-700 flex items-center gap-2" data-testid="sign-error">
                                <AlertCircle className="h-4 w-4" /> {signError}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={sign}
                            disabled={!signatureDataUrl || !approved || signing}
                            data-testid="sign-submit"
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-shariti-orange text-white h-14 font-black text-sm uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed hover:bg-shariti-black transition"
                        >
                            {signing
                                ? <><Loader2 className="h-4 w-4 animate-spin" /> Signature en cours…</>
                                : <><ShieldCheck className="h-4 w-4" /> Signer mon contrat</>
                            }
                        </button>
                        <div className="mt-3 text-center text-[11px] font-semibold text-slate-500 flex items-center justify-center gap-1.5">
                            <Lock className="h-3 w-3" /> Signature électronique conforme eIDAS · Horodatage + IP + empreinte cryptographique
                        </div>
                    </section>
                ) : (
                    <motion.section
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-3xl bg-emerald-500 text-white p-5 md:p-7 shadow-lg"
                        data-testid="signed-banner"
                    >
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-8 w-8 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="text-xl md:text-2xl font-black">Merci — contrat signé ✅</div>
                                <div className="text-sm font-medium text-white/90 mt-1">
                                    Nous vous avons envoyé une copie PDF à <strong>{c.email}</strong>.
                                    Conservez-la précieusement.
                                </div>
                                {justSigned?.sha256 && (
                                    <div className="mt-3 text-[10px] font-mono text-white/70 break-all">
                                        Empreinte SHA-256 : {justSigned.sha256}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.section>
                )}
            </main>

            <footer className="mt-16 text-center text-[11px] font-medium text-slate-500">
                © 2026 Shariti · Signature électronique horodatée conforme eIDAS
            </footer>
        </div>
    );
}


function SummaryTile({ Icon, label, value }) {
    return (
        <div className="rounded-2xl bg-shariti-cream/60 border border-shariti-gray/40 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Icon className="h-3 w-3 text-shariti-orange" /> {label}
            </div>
            <div className="text-sm font-extrabold text-shariti-black mt-1 break-words">{value}</div>
        </div>
    );
}

function fr(iso) {
    if (!iso) return "—";
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "long", year: "numeric" });
    } catch { return iso; }
}
